import React, { useEffect, useState } from "react";
import { TrendingUp, Package, Key, UserPlus, Link, Coins, RefreshCw } from "lucide-react";
import { getStoreId, getApiKey, getSettings, setSettings as setSessionSettings } from "../utils/storage";

const Dashboard = () => {
  const [settings, setSettings] = useState({
    apiKey: "",
    images_processed: 0,
    credits_available: 0,
    language: "en",
    length: "voice"
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [storeData, setStoreData] = useState(null);
  const [storeId, setStoreId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      }
      // Get ID from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      let id = urlParams.get("id");
      const storedId = getStoreId(); // Synchronous, do not await
      if (storedId) {
        id = storedId;
      }
      setStoreId(id);
      let currentStoreData = null;
      if (id) {
        // Fetch store data using the new API
        const apiUrl = import.meta.env.VITE_BACKEND_URL;
        const storeResponse = await fetch(`${apiUrl}/api/store-id?id=${id}`);
        if (storeResponse.ok) {
          const data = await storeResponse.json();
          currentStoreData = data;
          setStoreData(data);
        }
        // Fetch latest settings from backend
        const settingsResponse = await fetch(`${apiUrl}/api/settings?id=${id}`);
        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json();
          if (settingsData.success && settingsData.settings) {
            setSessionSettings(settingsData.settings); // update sessionStorage
            setSettings({
              apiKey: settingsData.settings.apiKey || "",
              images_processed: settingsData.settings.images_processed || 0,
              credits_available: settingsData.settings.credits_available || 0,
              language: settingsData.settings.language || "en",
              length: settingsData.settings.length || "voice"
            });
            setLoading(false);
            setRefreshing(false);
            return;
          }
        }
      }
      // Fallback to sessionStorage if backend call fails
      const apiKey = getApiKey() || "";
      const storedSettings = getSettings();
      setSettings({
        apiKey: apiKey,
        images_processed: storedSettings?.images_processed || 0,
        credits_available: storedSettings?.credits_available || 0,
        language: storedSettings?.language || "en",
        length: storedSettings?.length || "voice"
      });
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const handleSingleRegister = async () => {
    setIsSaving(true);

    console.log("handleSingleRegister->", storeId);
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/register?store_id=${storeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to connect');
      setNotification({ type: 'success', message: data.message || 'Your account is connected to AltTextify.net successfully!' });
      fetchDashboardData(); // Refresh dashboard data
    } catch (error) {
      setNotification({ type: 'error', message: error.message || 'Failed to connect. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="loading-dots mb-3" role="status" style={{ width: '24px', height: '24px', margin: '0 auto' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">

      <div className="row justify-content-center mb-4">
        <div className="col-12 col-md-12">
          <div className="card border-0 shadow-sm bg-opacity-10 p-4 mb-4 text-center">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-4">
              <div className="mb-3 mb-md-0">
                <img src="img/shortlogo.png" alt="AltTextify Logo" style={{ width: 100, height: 100, borderRadius: '50%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} />
              </div>
              <div>
                <h1 className="fw-bold mb-2 text-primary" style={{ fontSize: '1.75rem', letterSpacing: '-1px' }}>Welcome to AltTextify</h1>
                <h4 className="fw-semibold mb-3 text-dark">Effortless, AI-powered alt text for every image in your store</h4>
                <p className="lead text-secondary mb-0" style={{ maxWidth: 600, margin: '0 auto' }}>
                  Instantly boost your store's accessibility, SEO, and user experience. AltTextify generates high-quality, descriptive alt text for your product images—so you can focus on what matters most.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {storeData && settings.apiKey && settings.apiKey.trim() !== "" ? (
        // Connected User Dashboard
        <div className="row g-4">
          {/* Store Info */}
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between" role="alert">
              <div className="d-flex align-items-center">
                <div className="me-3">
                  {/* <strong>Connected Store:</strong> {storeData.store_id} */}
                </div>
              </div>
              <button 
                className="btn btn-outline-info btn-sm d-flex align-items-center"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw size={16} className={`me-1 ${refreshing ? 'spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh Credits'}
              </button>
            </div>
          </div>

          {/* API Key Status */}
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card h-100 border-0 shadow-sm hover-lift">
              <div className="card-body text-center p-4">
                <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
                  <Key size={32} className="text-success" />
                </div>
                <h5 className="card-title fw-bold mb-2">API Key Status</h5>
                <span className="badge bg-success fs-6 mb-2">Connected</span>
                <p className="card-text text-muted small mb-0">Your API key is active and ready</p>
              </div>
            </div>
          </div>

          {/* Images Processed */}
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card h-100 border-0 shadow-sm hover-lift">
              <div className="card-body text-center p-4">
                <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
                  <Package size={32} className="text-primary" />
                </div>
                <h5 className="card-title fw-bold mb-2">Images Processed</h5>
                <div className="display-6 fw-bold text-primary mb-2">
                  {settings.images_processed > 0 ? settings.images_processed.toLocaleString() : '-'}
                </div>
                <p className="card-text text-muted small mb-0">
                  {settings.images_processed > 0 ? 'Total images processed' : 'No data available'}
                </p>
              </div>
            </div>
          </div>

          {/* Credits Available */}
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card h-100 border-0 shadow-sm hover-lift">
              <div className="card-body text-center p-4">
                <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
                  <Coins size={32} className="text-success" />
                </div>
                <h5 className="card-title fw-bold mb-2">Credits Available</h5>
                <div className={`display-6 fw-bold mb-2 ${settings.credits_available === 0 ? 'text-danger' : 'text-success'}`}> 
                  {settings.credits_available > 0 ? settings.credits_available.toLocaleString() : '0'}
                </div>
                {settings.credits_available === 0 || settings.credits_available === "0.0" && (
                  <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: '0.875rem' }}>
                    <strong>Out of credits!</strong> Please <a href="https://alttextify.net/plans" target="_blank" rel="noopener noreferrer">buy more credits</a> to continue.
                  </div>
                )}
                <p className="card-text text-muted small mb-0">
                  {settings.credits_available != "0.0" ? 'Remaining credits': ''}
                </p>
              </div>
            </div>
          </div>

          {/* Need More Credits */}
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card h-100 border-0 shadow-sm hover-lift bg-light">
              <div className="card-body text-center p-4">
                <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
                  <TrendingUp size={32} className="text-warning" />
                </div>
                <h5 className="card-title fw-bold mb-2">Need More Credits?</h5>
                <p className="card-text text-muted mb-3">Top up your credits to keep generating alt text</p>
                <a 
                  href="https://alttextify.net/plans" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-lg px-4"
                >
                  Buy More Credits
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Setup Flow for New Users or Users without API Key
        <div className="row justify-content-center g-4">
          {notification && (
            <div className={`alert alert-${notification.type} mt-3`} role="alert">
              {notification.message}
            </div>
          )}
          <div className="col-12 col-md-6 mb-4">
            <div className="card h-100 border-0 shadow-sm p-5 text-center" style={{ background: '#e6f4ea', border: '2px solid #28a745' }}>
              <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 64, height: 64 }}>
                <UserPlus size={40} className="text-success" />
              </div>
              <h3 className="fw-bold mb-3 mt-2" style={{ color: '#28a745' }}>One-Click Connect with Store Email</h3>
              <p style={{ fontSize: '1.15rem', color: '#2d3a4a' }}>
                Register and connect instantly using your store email.<br />
                Just one click to get started!
              </p>
              <button
                className="btn btn-success btn-lg px-5 mt-2 shadow-sm"
                onClick={handleSingleRegister}
                disabled={isSaving}
              >
                Configure AltTextify With Store Email
              </button>
            </div>
          </div>
          <div className="col-12 col-md-6 mb-4">
            <div className="card h-100 border-0 shadow-sm p-5 text-center">
              <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 64, height: 64 }}>
                <Key size={40} className="text-primary" />
              </div>
              <h3 className="fw-bold mb-3" style={{ color: '#2d3a4a' }}>Connect with Any Email</h3>
              <p style={{ fontSize: '1.15rem', color: '#2d3a4a' }}>
                Prefer to use a different email? Just follow these 3 quick steps:
              </p>
              <div className="d-flex flex-column align-items-center gap-3">
                <button 
                  className="btn btn-outline-primary btn-lg px-4 w-75"
                  onClick={() => window.open(`https://alttextify.net/register?utm_source=ecwid&store_id=${storeId}&utm_medium=dl`, '_blank')}
                >
                  Step 1: Register
                </button>
                <button 
                  className="btn btn-outline-primary btn-lg px-4 w-75"
                  onClick={() => window.open('https://alttextify.net/settings/apikeys', '_blank')}
                >
                  Step 2: Create API Key
                </button>
                <button 
                  className="btn btn-outline-primary btn-lg px-4 w-75"
                  onClick={() => window.location.hash = 'settings'}
                >
                  Step 3: Connect Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 